// /subscribe - the mockup «Checkout» as the step before Stripe's hosted page (ADR-0046): the period, the summary and
// one button → POST /api/billing/checkout → the browser goes to Stripe. Signed in only, not pre-rendered.
// Who pays, the address and the card are Stripe's page - not drawn here. Stripe sells the subscription as merchant of
// record (ADR-0058): the summary names it as the seller and VALAS as the provider of the service (BL-177).
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router";
import { t } from "../i18n";
import { ApiError, getBillingState, startCheckout, type BillingPeriod, type BillingPlan, type PlanStateName } from "../api";
import { useSession } from "../session";
import { CheckIcon, LockIcon } from "./icons";
import { PAYMENT_PROCESSOR, SELLER, SELLER_ADDRESS } from "../seller";
import { CLIENTS_NEW, CLIENTS_WORD } from "../lib";

/** States with a live subscription: /subscribe and the Pricing button send them to Account. */
export const SUBSCRIBED: PlanStateName[] = ["active", "cancelled", "past-due", "paused"];

/** Stripe's minimum for `trial_end` (ADR-0046): with fewer days left the first charge is today. `days_left` is rounded up, so ≥ 3 is ≥ 48 h. */
export const keepsTrialDays = (plan: BillingPlan) =>
  (plan.state === "trial" || plan.state === "trial-ending") && (plan.days_left ?? 0) >= 3 && Boolean(plan.ends_on);

/** ADR-0059: during a gift Checkout starts the paid plan on the day the gift ends - unless less than 48 h are left (then it starts at once). */
export const keepsGiftDays = (plan: BillingPlan) => plan.state === "gift" && (plan.days_left ?? 0) >= 3 && Boolean(plan.ends_on);

/** "19 Oct 2026" out of "2026-10-19" (the date as written, no zone shift). */
export function longDay(isoDate: string): string {
  const d = new Date(`${isoDate.slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", day: "numeric", month: "short", year: "numeric" }).format(d);
}

/**
 * The plan of the signed-in user from GET /api/billing/state: undefined - loading or not signed in,
 * null - the server could not tell (the pages fall back to the plain path).
 */
export function useBillingPlan(signedIn: boolean): BillingPlan | null | undefined {
  const [plan, setPlan] = useState<BillingPlan | null | undefined>(undefined);
  useEffect(() => {
    if (!signedIn) return;
    let alive = true;
    getBillingState()
      .then((s) => alive && setPlan(s?.plan ?? null))
      .catch(() => alive && setPlan(null));
    return () => {
      alive = false;
    };
  }, [signedIn]);
  return plan;
}

const PRICE: Record<BillingPeriod, string> = { monthly: "€19.99", yearly: "€199" };
/** «Seller: Stripe … provided by …» of the summary - the values of web/src/seller.ts (BL-133, BL-177). */
const sellerNote = () =>
  t("subscribe.sellerNote", {
    seller: PAYMENT_PROCESSOR.name,
    role: PAYMENT_PROCESSOR.role,
    name: SELLER.tradeName,
    legal: SELLER.legalName,
    address: SELLER_ADDRESS,
  });

type View = "form" | "off" | "comp" | "subscribed";
const viewOf = (state: PlanStateName): View =>
  state === "off" ? "off" : state === "comp" ? "comp" : SUBSCRIBED.includes(state) ? "subscribed" : "form";

function Notice({ view }: { view: Exclude<View, "form"> }) {
  const [title, text] =
    view === "off"
      ? [t("subscribe.openSoon"), t("subscribe.openSoonText")]
      : view === "comp"
        ? [t("subscribe.comp"), t("subscribe.compText")]
        : [t("subscribe.subscribed"), t("subscribe.subscribedText")];
  return (
    <section className="sub-notice" data-testid={`subscribe-${view}`}>
      <h1>{title}</h1>
      <p className="hero-lead">{text}</p>
      {view !== "off" && (
        <Link className="btn btn-primary" to="/account">
          {t("subscribe.toAccount")}
        </Link>
      )}
    </section>
  );
}

export function SubscribePage() {
  const { me } = useSession();
  const loaded = useBillingPlan(Boolean(me));
  // The checkout answer can overrule the loaded state (409 billing_off / complimentary / already_subscribed).
  const [override, setOverride] = useState<View | null>(null);
  const [period, setPeriod] = useState<BillingPeriod>("monthly");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Back from Stripe: the browser restores this page from its cache with the button still «Opening Stripe…» - re-enable it.
  useEffect(() => {
    const reset = () => setBusy(false);
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, []);


  if (me === null) return <Navigate to={`/signin?return_to=${encodeURIComponent("/subscribe")}`} replace />;
  if (me === undefined || loaded === undefined) return null;
  if (loaded === null) return <div className="msg error sub-msg">{t("subscribe.loadFailed")}</div>;

  const view = override ?? viewOf(loaded.state);
  if (view !== "form") return <Notice view={view} />;

  const keep = keepsTrialDays(loaded) || keepsGiftDays(loaded);
  const date = loaded.ends_on ? longDay(loaded.ends_on) : "";
  const price = PRICE[period];
  const periodLabel = t(period === "yearly" ? "subscribe.everyYear" : "subscribe.everyMonth");
  const intro = keepsGiftDays(loaded)
    ? t("subscribe.giftLine", { date })
    : loaded.state === "gift"
      ? t("subscribe.giftShortLine", { date })
      : keep
    ? t("subscribe.trialLine", { days: loaded.days_left ?? 0, date })
    : loaded.state === "expired"
      ? t("subscribe.expiredLine")
      : t("subscribe.endingLine", { date });

  async function go() {
    setBusy(true);
    setError(null);
    try {
      const { url } = await startCheckout(period);
      window.location.assign(url);
    } catch (e) {
      setBusy(false);
      const code = e instanceof ApiError ? e.code : "";
      if (code === "billing_off") setOverride("off");
      else if (code === "complimentary") setOverride("comp");
      else if (code === "already_subscribed") setOverride("subscribed");
      else if (code === "too_many_checkouts") setError(t("subscribe.tooMany"));
      else if (code === "billing_error") setError(t("subscribe.unavailable"));
      else setError(t("subscribe.failed"));
    }
  }

  const [termsBefore, termsAfter] = t("subscribe.terms").split("{terms}");

  return (
    <div className="subscribe">
      <p className="sub-secure">
        <LockIcon size={14} />
        {t("subscribe.secure")}
      </p>
      <div className="sub-grid">
        <div className="sub-main">
          <header className="sub-head">
            <h1>{t("subscribe.h1")}</h1>
            <p>{intro}</p>
          </header>

          <section className="card elev-sm sub-card">
            <h2 id="sub-period">{t("subscribe.period")}</h2>
            <div role="radiogroup" aria-labelledby="sub-period" className="sub-periods">
              {(["monthly", "yearly"] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  role="radio"
                  aria-checked={period === p}
                  className="sub-period"
                  data-testid={`period-${p}`}
                  onClick={() => setPeriod(p)}
                >
                  <span className="sub-period-top">
                    <span>{t(p === "yearly" ? "pricing.yearly" : "pricing.monthly")}</span>
                    <span className="sub-period-price">{PRICE[p]}</span>
                  </span>
                  <span className="sub-period-hint">{t(p === "yearly" ? "subscribe.yearlyHint" : "subscribe.monthlyHint")}</span>
                </button>
              ))}
            </div>
            <p className="sub-next">{t("subscribe.nextStep")}</p>
          </section>

          <div className="sub-actions">
            {error && <div className="msg error">{error}</div>}
            <button type="button" className="btn btn-primary sub-go" disabled={busy} onClick={go} data-testid="subscribe-go">
              {busy ? t("subscribe.going") : t("subscribe.continue")}
            </button>
            <p className="sub-fine">
              {termsBefore}
              <a href="/terms.html">{t("subscribe.termsLink")}</a>
              {termsAfter}
            </p>
          </div>
        </div>

        <aside aria-label={t("subscribe.summary")} className="card elev-md sub-summary" data-testid="subscribe-summary">
          <div className="sub-sum-head">
            <span className="sub-label">{t("subscribe.summary")}</span>
            <span className="sub-product">{t("subscribe.product")}</span>
            <span className="sub-muted">{t("subscribe.included", { period: t(period === "yearly" ? "pricing.yearly" : "pricing.monthly") })}</span>
          </div>
          <dl className="sub-lines">
            <dt>{t(period === "yearly" ? "pricing.yearly" : "pricing.monthly")}</dt>
            <dd>{price}</dd>
            <dt>{t("subscribe.vat")}</dt>
            <dd className="sub-muted">{t("subscribe.vatNext")}</dd>
          </dl>
          <div className="sub-rule" />
          <div className="sub-total">
            <span>{keep ? t("subscribe.dueOn", { date }) : t("subscribe.dueToday")}</span>
            <span className="sub-total-sum">{price}</span>
          </div>
          <p className="sub-schedule" data-testid="subscribe-schedule">
            {keep
              ? t("subscribe.scheduleTrial", { price, every: periodLabel, date })
              : t("subscribe.scheduleNow", { price, every: periodLabel })}
          </p>
          <ul className="sub-notes">
            {/* BL-194: the same line as on /pricing - clients are in the same price (ADR-0062). */}
            <li data-testid="subscribe-clients">
              <CheckIcon size={14} />
              <span>
                {t("pricing.incl.clients", { count: CLIENTS_WORD })}
                {" · "}
                <Link className="text-link" to="/clients/more" data-testid="subscribe-incl-more">
                  {t("pricing.incl.clientsMore")}
                </Link>
                {CLIENTS_NEW && <span className="sub-new">{t("pricing.new")}</span>}
              </span>
            </li>
            {[t("subscribe.cancelNote"), t("subscribe.invoiceNote"), sellerNote()].map((note) => (
              <li key={note}>
                <CheckIcon size={14} />
                <span>{note}</span>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  );
}
